import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Users, FolderKanban, Mail, TrendingUp } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import { api } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import AdminHeader from "../../components/AdminHeader";
import { statusLabel } from "../../lib/statusI18n";

const PRIORITY_COLOR = { "Urgente": "#00F0FF", "Haute": "#0047FF", "Moyenne": "#8E9DB8", "Basse": "#4A5A7D", "Non définie": "#1c2b4a" };

export default function AdminAnalytics() {
  const [a, setA] = useState(null);
  const [period, setPeriod] = useState("day");
  const [traffic, setTraffic] = useState(null);
  const nav = useNavigate();
  const { tf: t } = useLang();
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/admin/analytics/traffic", { params: { period } }).then((r) => setTraffic(r.data)).catch(() => {});
  }, [period]);
  if (!a) return <div className="py-20 text-center text-ink-secondary">{t("adm_loading")}</div>;

  const PERIODS = [
    { k: "hour", l: "Heure" },
    { k: "day", l: "Jour" },
    { k: "month", l: "Mois" },
    { k: "year", l: "Année" },
  ];
  const PERIOD_SUB = { hour: "Dernières 24 heures", day: "30 derniers jours", month: "12 derniers mois", year: "6 dernières années" };

  const kpis = [
    { icon: Eye, v: a.total_views, l: t("an_kpi_views"), s: `${a.views_today} ${t("an_today")}` },
    { icon: Users, v: a.users_total, l: t("an_kpi_users"), s: t("an_registered") },
    { icon: FolderKanban, v: a.projects_total, l: t("an_kpi_requests"), s: t("an_excl_drafts") },
    { icon: Mail, v: a.contacts_total, l: t("an_kpi_contacts"), s: t("an_received") },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("an_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white sm:text-4xl">{t("an_title")}</h1>

      {/* Section « indicateurs » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-indicateurs">Indicateurs</div>
      <div className="mt-6 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="analytics-panel-indicateurs">
        {kpis.map((k, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="bg-void p-6" data-testid={`analytics-kpi-${i}`}>
            <k.icon size={20} className="text-cyan" strokeWidth={1.5} />
            <div className="mt-5 font-display text-4xl font-900 tabular-nums text-white">{k.v}</div>
            <div className="mt-1 text-sm text-ink-secondary">{k.l}</div>
            <div className="text-xs text-ink-tertiary">{k.s}</div>
          </motion.div>
        ))}
      </div>
      </>

      {/* Traffic over time */}
      {/* Section « trafic » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-trafic">Trafic</div>
      <div className="mt-6 border border-white/10 bg-surface p-6" data-testid="analytics-traffic">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-600 text-white"><TrendingUp size={18} className="text-cyan" /> Trafic du site</div>
            <div className="mt-1 text-xs text-ink-tertiary">{PERIOD_SUB[period]}{traffic ? ` · ${traffic.total} vues` : ""}</div>
          </div>
          <div className="inline-flex rounded-full border border-white/10 bg-void p-1" data-testid="analytics-period-switch">
            {PERIODS.map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`analytics-period-${p.k}`}
                className={`rounded-full px-4 py-1.5 text-xs font-600 uppercase tracking-wide transition-all ${period === p.k ? "bg-cyan text-void" : "text-ink-secondary hover:text-white"}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={traffic?.series || []} margin={{ left: -20, right: 10 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#4A5A7D" fontSize={11} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
              <YAxis stroke="#4A5A7D" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0D152C", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} />
              <Area type="monotone" dataKey="count" stroke="#00F0FF" strokeWidth={2} fill="url(#g)" name={t("an_views")} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      </>

      {/* Section « repartition » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-repartition">Répartition</div>
      <div className="mt-6 grid gap-8 lg:grid-cols-2" data-testid="analytics-panel-repartition">
        {/* Priority distribution */}
        <div className="border border-white/10 bg-surface p-6" data-testid="analytics-priority">
          <div className="mb-6 text-sm font-600 text-white">{t("an_by_priority")}</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={a.by_priority} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="name" stroke="#4A5A7D" fontSize={11} tickLine={false} />
                <YAxis stroke="#4A5A7D" fontSize={11} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#0D152C", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} />
                <Bar dataKey="count" name={t("an_requests")}>
                  {a.by_priority.map((e, i) => <Cell key={i} fill={PRIORITY_COLOR[e.name] || "#0047FF"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top pages */}
        <div className="border border-white/10 bg-surface p-6" data-testid="analytics-toppages">
          <div className="mb-4 text-sm font-600 text-white">{t("an_top_pages")}</div>
          <div className="space-y-2">
            {a.top_pages.map((p, i) => {
              const max = a.top_pages[0]?.count || 1;
              return (
                <div key={i}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-ink-secondary">{p.path}</span>
                    <span className="tabular-nums text-cyan">{p.count}</span>
                  </div>
                  <div className="h-1.5 bg-elevated"><div className="h-full bg-cyan" style={{ width: `${(p.count / max) * 100}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </>

      {/* Status breakdown */}
      {/* Section « statuts » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-statuts">Statuts</div>
      <div className="mt-6 border border-white/10 bg-surface p-6" data-testid="analytics-status">
        <div className="mb-4 text-sm font-600 text-white">{t("an_by_status")}</div>
        <div className="flex flex-wrap gap-3">
          {a.by_status.map((s, i) => (
            <button key={i} onClick={() => nav(`/admin/projets?status=${encodeURIComponent(s.name)}`)} className="border border-white/10 bg-void px-4 py-3 text-left transition-colors hover:border-cyan">
              <div className="font-display text-2xl font-900 tabular-nums text-white">{s.count}</div>
              <div className="mt-0.5 text-[11px] uppercase tracking-wide text-ink-tertiary">{statusLabel(t, s.name)}</div>
            </button>
          ))}
        </div>
      </div>
      </>
    </div>
  );
}
